import Flatpickr from "stimulus-flatpickr";
import { French } from "flatpickr/dist/l10n/fr.js";
import monthSelectPlugin from "flatpickr/dist/plugins/monthSelect/index.js";
import "flatpickr/dist/plugins/monthSelect/style.css";

export default class extends Flatpickr {
  static values = {
    minDate: String,
    maxDate: String,
    enableTime: { type: Boolean, default: false },
    noCalendar: { type: Boolean, default: false },
    dateFormat: { type: String, default: "Y-m-d" },
    altFormat: String,
    // Sélecteur d'heure : heure proposée à l'ouverture d'un champ vide, et pas
    // des minutes (flatpickr : 12 h et 5 min par défaut).
    defaultHour: Number,
    minuteIncrement: Number,
    // Un mois entier (« novembre 2026 ») : le champ envoie `2026-11`.
    monthOnly: { type: Boolean, default: false },
    // Where the calendar opens: "below" (default), "above", or "auto" — above
    // when there is no room under the field (a field at the foot of a page).
    position: { type: String, default: "below" },
  };

  initialize() {
    // Configuration minimale - utilise les styles par défaut du thème
    this.config = {
      locale: French,
      time_24hr: true,
      altInput: true,
      altFormat: this.hasAltFormatValue
        ? this.altFormatValue
        : this.enableTimeValue ? (this.noCalendarValue ? "H:i" : "d/m/Y H:i") : "d/m/Y",
      enableTime: this.enableTimeValue,
      noCalendar: this.noCalendarValue,
      dateFormat: this.dateFormatValue,
      allowInput: true,
      // Fix pour la navigation entre les années
      monthSelectorType: "dropdown",
      // Permet de changer l'année facilement
      yearSelectorType: "dropdown",
      // Le calendrier s'ouvre sous le champ. Il reste attaché au <body>
      // (mode par défaut) : `static: true` ferait déplacer le champ dans un
      // wrapper créé par flatpickr, ce qui casse le DOM ici puisque le
      // contrôleur détruit et recrée l'instance à chaque connexion Stimulus.
      position: this.positionValue,
    };

    if (this.monthOnlyValue) {
      this.config.plugins = [monthSelectPlugin({ shorthand: true, dateFormat: "Y-m", altFormat: "F Y" })];
      this.config.allowInput = false;
    }

    if (this.hasDefaultHourValue) this.config.defaultHour = this.defaultHourValue;
    if (this.hasMinuteIncrementValue) this.config.minuteIncrement = this.minuteIncrementValue;

    // Min/Max dates
    if (this.hasMinDateValue) {
      this.config.minDate = this.minDateValue;
    }

    if (this.hasMaxDateValue) {
      this.config.maxDate = this.maxDateValue;
    }

    // Default date
    if (this.element.value) {
      this.config.defaultDate = this.element.value;
    }
  }

  connect() {
    // Prevent double initialization
    if (this.element._flatpickr) {
      this.element._flatpickr.destroy();
    }

    super.connect();

    // Le calendrier est attaché au <body> et sa position est calculée une seule
    // fois à l'ouverture : sans cela il resterait figé et se détacherait du
    // champ dès que la page défile. On la recalcule pendant le défilement.
    this.repositionCalendar = () => {
      const picker = this.element._flatpickr;
      if (picker?.isOpen) picker._positionCalendar();
    };
    window.addEventListener("scroll", this.repositionCalendar, true);
    window.addEventListener("resize", this.repositionCalendar);
  }

  disconnect() {
    if (this.repositionCalendar) {
      window.removeEventListener("scroll", this.repositionCalendar, true);
      window.removeEventListener("resize", this.repositionCalendar);
    }
    super.disconnect();
  }
}
